/* eslint-disable indent */
import { createExportHandlers } from '@src/main/modules/messages/utils/createExportHandlers';
import { normalizeFleet } from '@src/main/modules/messages/utils/formatters';
import { Fleet } from '@src/main/modules/parameters/domain/fleet-positions/fleet.interface';
import FleetForm from '@src/main/modules/parameters/ui/fleet-positions/components/fleets/FleetForm';
import { getFleetSchema } from '@src/main/modules/parameters/ui/fleet-positions/fleetSchema';
import { useCreateFleet } from '@src/main/modules/parameters/ui/fleet-positions/hooks/useCreateFleet';
import { useDeleteFleet } from '@src/main/modules/parameters/ui/fleet-positions/hooks/useDeleteFleet';
import { useFleets } from '@src/main/modules/parameters/ui/fleet-positions/hooks/useFleets';
import { useUpdateFleet } from '@src/main/modules/parameters/ui/fleet-positions/hooks/useUpdateFleets';
import { PERMISSIONS } from '@src/main/shared/constants/permissions';
import { useAuth } from '@src/main/shared/context/AuthContext';
import { useToast } from '@src/main/shared/context/ToastContext';
import AppDialog from '@src/main/shared/ui/components/dialog/AppDialog';
import ConfirmDeleteDialog from '@src/main/shared/ui/components/dialog/ConfirmDeleteDialog';
import HeaderTitle from '@src/main/shared/ui/components/header/HeaderTitle';
import AppLoading from '@src/main/shared/ui/components/spinner/AppLoading';
import AppTable from '@src/main/shared/ui/components/table/AppTable';
import TableHeader from '@src/main/shared/ui/components/table/TableHeader';
import { formatList } from '@src/main/shared/utils/formatList';
import { showErrorMessage, showSuccessMessage } from '@src/main/shared/utils/toastUtils';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

interface FleetRow extends Fleet {
  id: string;
}

const FleetsSection: FC = () => {
  const { t } = useTranslation();

  const { hasPermission } = useAuth();
  const hasEditPermissions = hasPermission(PERMISSIONS.PARAMETERS.POSITIONS_FLEETS.edit);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedFleets, setSelectedFleets] = useState<FleetRow[]>([]);
  const { data = [], isLoading: loadingFleets } = useFleets();

  const queryClient = useQueryClient();
  const toast = useToast();

  const { mutateAsync: createFleet } = useCreateFleet();
  const { mutateAsync: updateFleet } = useUpdateFleet();
  const { mutateAsync: deleteFleet } = useDeleteFleet();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newEditFleet, setNewEditFleet] = useState<Fleet>({
    fleetId: '',
    fleet: '',
    description: '',
    positions: [],
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [fleetToDelete, setFleetToDelete] = useState<Fleet | null>(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);

  const toolbarVisibility = [
    true,
    hasEditPermissions,
    selectedFleets.length > 0,
    true,
    false,
    false,
  ];

  const fleetsData: FleetRow[] = data.map((f) => ({
    id: f.fleetId!,
    ...f,
  }));

  const FILE_NAME = t('parameters.fleetsAndPositions.fleets.fileName');
  const { onExportExcel, onExportPdf } = createExportHandlers(data, normalizeFleet, FILE_NAME, t);

  const handleShowCreateFleet = () => {
    setNewEditFleet({
      fleetId: '',
      fleet: '',
      description: '',
      positions: [],
    });
    setValidationErrors({});
    setIsModalVisible(true);
  };

  const handleCreateEdit = async () => {
    const schema = getFleetSchema(t);

    try {
      schema.parse(newEditFleet);

      if (newEditFleet.fleetId) {
        await updateFleet({
          id: newEditFleet.fleetId,
          data: {
            fleet: newEditFleet.fleet,
            description: newEditFleet.description,
          },
        });
        showSuccessMessage(toast, t('parameters.fleetsAndPositions.fleets.updatedSuccess'));
      } else {
        await createFleet({
          fleet: newEditFleet.fleet,
          description: newEditFleet.description,
        });
        showSuccessMessage(toast, t('parameters.fleetsAndPositions.fleets.createdSuccess'));
      }

      await queryClient.invalidateQueries({ queryKey: ['fleet'] });
      setIsModalVisible(false);
      setValidationErrors({});
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path.length > 0) {
            const key = err.path[0];
            errors[key] = err.message;
          }
        });
        setValidationErrors(errors);
      } else {
        showErrorMessage(toast, error);
      }
    }
  };

  const handleEditFleet = (row: FleetRow) => {
    setNewEditFleet({
      fleetId: row.fleetId,
      fleet: row.fleet,
      description: row.description,
      positions: row.positions ?? [],
    });
    setValidationErrors({});
    setIsModalVisible(true);
  };

  const handleChange = (field: keyof Fleet, value: string) => {
    setNewEditFleet((prev) => ({ ...prev, [field]: value }));
  };

  const handleDeleteFleet = (row: FleetRow) => {
    const fleet: Fleet = {
      fleetId: row.fleetId,
      fleet: row.fleet,
      description: row.description,
      positions: row.positions ?? [],
    };
    setFleetToDelete(fleet);
  };

  const deleteSelectedFleets = () => {
    if (selectedFleets.length === 0) return;
    setIsBulkDelete(true);
  };

  const confirmDelete = async () => {
    try {
      if (isBulkDelete) {
        await Promise.all(selectedFleets.map((fleet) => deleteFleet(fleet.fleetId!)));
        setSelectedFleets([]);
      } else if (fleetToDelete) {
        await deleteFleet(fleetToDelete.fleetId!);
        setSelectedFleets((prev) => prev.filter((f) => f.fleetId !== fleetToDelete.fleetId));
      }

      await queryClient.invalidateQueries({ queryKey: ['fleet'] });
      showSuccessMessage(toast, t('parameters.fleetsAndPositions.fleets.deletedSuccess'));
    } catch (error) {
      showErrorMessage(toast, error);
    } finally {
      setFleetToDelete(null);
      setIsBulkDelete(false);
    }
  };

  const rejectDelete = () => {
    setFleetToDelete(null);
    setIsBulkDelete(false);
  };

  return (
    <div className="flex flex-col gap-2">
      <HeaderTitle title={t('parameters.fleetsAndPositions.fleets.title')} />
      <TableHeader
        onCreate={handleShowCreateFleet}
        onDelete={deleteSelectedFleets}
        onRefresh={async () => {
          setIsRefreshing(true);
          setTimeout(() => setIsRefreshing(false), 500);
        }}
        onExportExcel={onExportExcel}
        onExportPdf={onExportPdf}
        visibility={toolbarVisibility}
        exportable
      />
      <AppLoading isLoading={loadingFleets || isRefreshing}>
        <AppTable
          headers={[
            { name: t('parameters.fleetsAndPositions.fleets.fleet'), field: 'fleet' },
            { name: t('parameters.fleetsAndPositions.fleets.description'), field: 'description' },
          ]}
          data={fleetsData}
          onEdit={hasEditPermissions ? handleEditFleet : undefined}
          onDelete={hasEditPermissions ? handleDeleteFleet : undefined}
          onSelectedChange={hasEditPermissions ? setSelectedFleets : undefined}
        />
      </AppLoading>
      <AppDialog
        visible={isModalVisible}
        title={
          newEditFleet.fleetId
            ? t('parameters.fleetsAndPositions.fleets.updateFleet', { fleet: newEditFleet.fleet })
            : t('parameters.fleetsAndPositions.fleets.createFleet')
        }
        onClose={() => setIsModalVisible(false)}
        disableButtons={loadingFleets}
        onConfirm={handleCreateEdit}
      >
        <FleetForm values={newEditFleet} handleChange={handleChange} errors={validationErrors} />
      </AppDialog>
      <ConfirmDeleteDialog
        confirmDelete={confirmDelete}
        rejectDelete={rejectDelete}
        visible={isBulkDelete || fleetToDelete !== null}
        message={
          isBulkDelete
            ? t('parameters.fleetsAndPositions.fleets.confirmBulkDelete', {
                fleets: formatList(
                  selectedFleets.map((f) => f.fleet),
                  t('common.and'),
                ),
              })
            : t('parameters.fleetsAndPositions.fleets.confirmDelete', {
                fleet: fleetToDelete?.fleet,
              })
        }
      />
    </div>
  );
};

export default FleetsSection;
